import type { BadgeVariant } from "./badge.variants";

const STATUS_MAP: Record<string, BadgeVariant> = {
  // success
  accepted: "success",
  approved: "success",
  auto_approved: "success",
  verified: "success",
  active: "success",
  resolved: "success",
  completed: "success",
  paid: "success",
  signed: "success",
  enabled: "success",
  success: "success",
  applied: "success",
  present: "success",
  // warning
  hold: "warning",
  pending: "warning",
  pending_confirmation: "warning",
  awaiting_confirmation: "warning",
  // Awaiting a decision — same "action needed" weight as `pending`.
  submitted: "warning",
  open: "warning",
  in_progress: "warning",
  "in-progress": "warning",
  "in-review": "warning",
  in_review: "warning",
  "expiring-soon": "warning",
  draft: "warning",
  generating: "warning",
  // Chat scheduled message being sent right now.
  sending: "warning",
  overtime: "warning",
  not_configured: "warning",
  incomplete: "warning",
  past_due: "warning",
  // Device attendance event processing status. `pending`, `applied` and
  // `failed` are already covered above.
  unmapped: "warning",
  duplicate: "neutral",
  ignored: "neutral",
  // danger
  rejected: "danger",
  expired: "danger",
  inactive: "danger",
  offboarded: "danger",
  failed: "danger",
  disabled: "danger",
  absent: "danger",
  overdue: "danger",
  unpaid: "danger",
  faulty: "danger",
  suspended: "danger",
  unreachable: "danger",
  // info
  new: "info",
  read: "neutral",
  assigned: "info",
  processed: "info",
  review_update: "info",
  request_update: "info",
  changes_requested: "warning",
  scheduled: "info",
  upcoming: "info",
  leave: "info",
  trialing: "info",
  reviewed: "info",
  added_to_pool: "success",
  added_to_pipeline: "success",
  strong_hire: "success",
  strong_shortlist: "success",
  hire: "success",
  no_hire: "danger",
  not_a_fit: "danger",
  recommend_shortlist: "info",
  "consider_·_hold": "warning",
  "advance_/_make_offer": "success",
  strong_backup: "info",
  sent: "info",
  viewed: "info",
  // primary
  processing: "primary",
  queued: "info",
  pending_mapping: "warning",
  connected: "success",
  checkout: "primary",
  instant: "primary",
  // neutral — "cancelled" is a terminal, user/system-initiated state, not an error.
  // Use rejected/failed for error states. Call-sites needing red for a cancelled
  // record can still pass an explicit variant.
  excluded: "neutral",
  not_started: "neutral",
  cancelled: "neutral",
  canceled: "neutral",
  cancel_pending: "warning",
  closed: "neutral",
  unassigned: "neutral",
  retired: "neutral",
  archived: "neutral",
  onboarding: "neutral",
  refunded: "neutral",
  paused: "neutral",
  no_subscription: "neutral",
  revoked: "neutral",
  // A document update replaced by a newer one before it was reviewed.
  superseded: "neutral",
  // Assessments. `draft`, `archived`, `submitted`, `in_progress`, `completed`,
  // `failed` and `hold` are already covered above.
  published: "success",
  invited: "neutral",
  ai_done: "info",
  human_done: "primary",
  final: "success",
  proceed: "success",
  "proceed with notes": "warning",
  reject: "danger",
  "passed — proceed to interview": "success",
  "on hold": "warning",
  "not passed": "danger",
  advance: "success",
  "advance with notes": "warning",
  default: "neutral",
  // Data Transfer run lifecycle. `queued`, `processing`, `pending_mapping`,
  // `completed`, `failed`, `cancelled`, `incomplete`, `excluded` and `warning`
  // are already covered above.
  uploaded: "info",
  analyzing: "primary",
  validating: "primary",
  validated: "info",
  validation_failed: "danger",
  partially_completed: "warning",
  undoing: "warning",
  undone: "neutral",
  error: "danger",
  // Audit log severity.
  info: "info",
  warning: "warning",
  critical: "danger",
  // Audit log request outcome (derived from the row's HTTP status code).
  // `success` and `failed` are already covered above.
  denied: "danger",
  // Face camera enrollment sync. `pending` and `failed` are covered above.
  synced: "success",
  removing: "neutral",
};

export function statusToVariant(status: string | null | undefined): BadgeVariant {
  if (!status) return "neutral";
  const key = String(status).toLowerCase().replace(/\s+/g, "_");
  return STATUS_MAP[key] ?? STATUS_MAP[key.replace(/_/g, "-")] ?? "neutral";
}
